import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { evento } from '@/lib/evento';
import { getAcquisition, acquisitionRegion, trialRegistrationUrl } from '@/lib/acquisition';
import { trackTrialStarted } from '@/lib/googleAdsConversions';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, CreditCard, Building2, Bell, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Precios() {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(null);

  useEffect(() => { evento('precios_visto'); }, []);
  const [ofertaIntro, setOfertaIntro] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/pagos/oferta-intro`, { withCredentials: true }).then(r => setOfertaIntro(!!r.data.disponible)).catch(() => {});
  }, [isAuthenticated]);

  const [prueba, setPrueba] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/pagos/prueba`, { withCredentials: true }).then(r => setPrueba(r.data)).catch(() => {});
  }, [isAuthenticated]);
  const iniciarPrueba = async (planId) => {
    setLoading(planId);
    try {
      const r = await axios.post(`${API}/pagos/iniciar-prueba`, { plan: planCheckout(planId) }, { withCredentials: true });
      if (r.data.started === true) trackTrialStarted({ started: true, plan: r.data.plan, ends_at: r.data.termina });
      evento('prueba_iniciada');
      toast.success(`¡Listo! Tienes ${r.data.dias} días gratis de ${planId === 'cotizador' ? 'Cotizador IA' : 'CotizaBot'}`);
      window.location.href = planId === 'cotizador' ? '/cotizador' : '/onboarding';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo activar la prueba');
    } finally { setLoading(null); }
  };

  const handleUpgrade = async (planId) => {
    evento('checkout_iniciado');
    if (!isAuthenticated) {
      toast.info('Primero necesitas crear una cuenta');
      return;
    }

    try {
      setLoading(planId);
      
      const response = await axios.post(`${API}/pagos/crear-checkout`, {
        plan: planCheckout(planId),
        success_url: `${window.location.origin}/pago-exitoso`,
        cancel_url: `${window.location.origin}/precios`,
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creando checkout:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setLoading(null);
    }
  };

  const planActual = user?.empresa?.plan || user?.usuario?.plan || null;

  const esUS = acquisitionRegion(getAcquisition(), localStorage.getItem('cotizador_region')) === 'US';
  const USD_PRECIOS = { cotizador: 29, cotizabot: 49, pro: 99 };
  const planCheckout = (planId) => (esUS && USD_PRECIOS[planId] ? planId + '_usa' : planId);

  const planes = [
    {
      id: 'cotizador',
      nombre: 'Cotizador IA',
      precio: 299,
      descripcion: 'Haz tus cotizaciones en segundos — sin conectar WhatsApp',
      features: [
        { texto: 'Pega la lista del cliente y la IA la cotiza', incluido: true, destacado: true },
        { texto: 'Tu catálogo desde Excel (ilimitado)', incluido: true },
        { texto: 'PDF con folio, logo y color de tu marca', incluido: true },
        { texto: 'Link para compartir por WhatsApp normal', incluido: true },
        { texto: 'Historial de cotizaciones', incluido: true },
        { texto: 'Bot que contesta solo en WhatsApp', incluido: false, nota: 'Disponible en CotizaBot' },
      ],
      popular: false,
      btnTexto: 'Empezar con Cotizador',
    },
    {
      id: 'cotizabot',
      nombre: 'CotizaBot',
      precio: 1000,
      descripcion: 'Responde pedidos y cotiza por WhatsApp con tu catálogo',
      features: [
        { texto: 'Cotizaciones ilimitadas', incluido: true },
        { texto: 'WhatsApp Business integrado', incluido: true },
        { texto: 'Código QR y link propio', incluido: true },
        { texto: 'Dashboard completo', incluido: true },
        { texto: 'Soporte prioritario', incluido: true },
        { texto: 'Cobros automáticos', incluido: false, nota: 'Disponible en CotizaBot Pro' },
      ],
      popular: true,
      btnTexto: 'Empezar Ahora',
    },
    {
      id: 'pro',
      nombre: 'CotizaBot Pro',
      precio: 2000,
      descripcion: '¡Cobra directamente por WhatsApp!',
      badge: '💰 COBRA A TUS CLIENTES',
      features: [
        { texto: 'Todo de CotizaBot', incluido: true },
        { texto: 'Link de pago Mercado Pago', incluido: true, destacado: true },
        { texto: 'Datos SPEI automáticos', incluido: true, destacado: true },
        { texto: 'Notificaciones de pago', incluido: true, destacado: true },
        { texto: 'Configura tu CLABE', incluido: true },
        { texto: 'Recibe pagos 24/7', incluido: true },
      ],
      popular: false,
      btnTexto: 'Quiero Cobrar',
      destacado: true,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Helmet>
        <title>Precios CotizaBot — Prueba 7 días gratis · Cotizador desde $299 MXN</title>
        <meta name="description" content="Prueba cualquier plan 7 días gratis, sin tarjeta. Cotizador IA $299 MXN/mes (primer mes $99), CotizaBot $1,000 MXN/mes y CotizaBot Pro con cobros $2,000 MXN/mes." />
        <link rel="canonical" href="https://cotizaexpress.com/precios" />
        <meta property="og:title" content="Precios CotizaBot - Planes desde $1,000 MXN/mes" />
        <meta property="og:description" content="Automatiza cotizaciones por WhatsApp con CotizaBot. CotizaBot y CotizaBot Pro con cobros integrados." />
        <meta property="og:url" content="https://cotizaexpress.com/precios" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://cotizaexpress.com/og-cotizabot.png" />
        <meta property="og:locale" content="es_MX" />
      </Helmet>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/logo-cotizaexpress.png" 
                alt="CotizaBot by CotizaExpress.com" 
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-slate-900">CotizaBot</span>
                <span className="text-xs text-slate-500 block -mt-1">by CotizaExpress.com</span>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">
                    Ir al Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="text-slate-600">
                      Iniciar Sesión
                    </Button>
                  </Link>
                  <Link to="/registro">
                    <Button className="bg-emerald-600 hover:bg-emerald-700">
                      Comenzar
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              Precios Simples y Transparentes
            </h1>
            <p className="text-lg sm:text-xl text-slate-600">
              Precios claros, sin sorpresas. <strong>Cobra a tus clientes</strong> con CotizaBot Pro.
            </p>
          </div>

          <p className="text-center -mt-8 mb-10 text-emerald-700 font-semibold">✅ Prueba cualquier plan 7 días gratis · sin tarjeta · al terminar se corta y decides si pagas</p>
          {prueba?.activa && (
            <div className="max-w-2xl mx-auto mb-6 bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-4 text-center">
              <p className="font-bold text-slate-900">🧪 Estás probando {prueba.plan?.startsWith('cotizador') ? 'Cotizador IA' : 'CotizaBot'} — te quedan {prueba.dias_restantes} día(s)</p>
              <p className="text-sm text-slate-600">Al terminar, tu cuenta pasa al plan gratis. Actívalo antes para no cortar.</p>
            </div>
          )}
          {ofertaIntro && (
            <div className="max-w-2xl mx-auto mb-10 bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 text-center">
              <p className="text-sm font-bold text-amber-800">🎁 Oferta de bienvenida para tu cuenta</p>
              <p className="text-2xl font-extrabold text-slate-900 mt-1">Cotizador IA: tu primer mes a {esUS ? '$9 USD' : '$99'}</p>
              <p className="text-sm text-slate-600 mb-4">Cotizaciones ilimitadas por 30 días. Después {esUS ? '$29 USD' : '$299'}/mes solo si quieres seguir — sin cargos automáticos.</p>
              <Button size="lg" onClick={() => handleUpgrade(esUS ? 'cotizador_usa_intro' : 'cotizador_intro')} disabled={loading !== null} className="bg-violet-600 hover:bg-violet-700 font-bold">
                Activar por {esUS ? '$9 USD' : '$99'} →
              </Button>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {planes.map((plan) => {
              const esActual = planActual === plan.id && !prueba?.activa;
              const esDestacado = plan.destacado;
              const esPopular = plan.popular;
              
              return (
                <Card 
                  key={plan.id}
                  className={`relative overflow-hidden transition-all ${
                    esDestacado 
                      ? 'border-2 border-amber-400 shadow-xl shadow-amber-100 scale-105' 
                      : esActual 
                        ? 'border-2 border-emerald-400 bg-emerald-50' 
                        : esPopular 
                          ? 'border-2 border-emerald-300'
                          : 'border border-slate-200'
                  }`}
                  data-testid={`plan-${plan.id}-card`}
                >
                  {/* Badge superior */}
                  {esActual && (
                    <div className="bg-emerald-500 text-white text-center py-1 text-sm font-medium">
                      Tu plan actual
                    </div>
                  )}
                  {!esActual && esPopular && (
                    <div className="bg-emerald-500 text-white text-center py-1 text-sm font-medium">
                      Recomendado
                    </div>
                  )}
                  {!esActual && esDestacado && (
                    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center py-1.5 text-sm font-bold">
                      {plan.badge}
                    </div>
                  )}

                  <CardContent className="pt-6 pb-8">
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-slate-900 mb-1">{plan.nombre}</h3>
                      <p className="text-sm text-slate-500 mb-4">{plan.descripcion}</p>
                      
                      <div className={`text-4xl font-bold mb-1 ${esDestacado ? 'text-amber-600' : 'text-emerald-600'}`}>
                        ${esUS && USD_PRECIOS[plan.id] ? USD_PRECIOS[plan.id] : plan.precio.toLocaleString()}
                      </div>
                      {plan.precio > 0 ? (
                        <p className="text-sm text-slate-500 mb-6">{esUS && USD_PRECIOS[plan.id] ? 'USD/mes (se cobra el equivalente en MXN)' : 'MXN/mes'}</p>
                      ) : (
                        <p className="text-sm text-slate-500 mb-6">Para siempre</p>
                      )}

                      {/* Features */}
                      <ul className="text-left space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            {feature.incluido ? (
                              <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${feature.destacado ? 'text-amber-500' : 'text-emerald-500'}`} />
                            ) : (
                              <X className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />
                            )}
                            <span className={`text-sm ${
                              feature.incluido 
                                ? feature.destacado 
                                  ? 'text-amber-700 font-medium' 
                                  : 'text-slate-700'
                                : 'text-slate-400'
                            }`}>
                              {feature.texto}
                              {feature.nota && (
                                <span className="block text-xs text-slate-400">{feature.nota}</span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>

                      {/* Botón */}
                      {esActual ? (
                        <Button variant="outline" className="w-full" disabled>
                          Plan Activo
                        </Button>
                      ) : (
                        isAuthenticated && prueba?.disponible && !(prueba?.activa && planActual === plan.id) ? (
                          <div className="space-y-2">
                            <Button
                              className={`w-full ${esDestacado ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                              onClick={() => iniciarPrueba(plan.id)}
                              disabled={loading === plan.id}
                            >
                              {loading === plan.id ? 'Activando…' : (prueba?.activa ? `Cambiar mi prueba a este plan` : 'Probar 7 días gratis')}
                            </Button>
                            <button onClick={() => handleUpgrade(plan.id)} className="w-full text-xs text-slate-500 underline">o actívalo ya</button>
                          </div>
                        ) : isAuthenticated ? (
                          <Button 
                            className={`w-full ${esDestacado ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            onClick={() => handleUpgrade(plan.id)}
                            disabled={loading === plan.id}
                            data-testid={`btn-upgrade-${plan.id}`}
                          >
                            {loading === plan.id ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Procesando...
                              </>
                            ) : (
                              <>
                                {plan.btnTexto}
                              </>
                            )}
                          </Button>
                        ) : (
                          <Link to={trialRegistrationUrl(plan.id)} className="block">
                            <Button className={`w-full ${esDestacado ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                              Probar 7 días gratis
                            </Button>
                            <p className="text-[11px] text-slate-400 mt-1 text-center">Sin tarjeta · al terminar se corta, pagas solo si quieres seguir</p>
                          </Link>
                        )
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* CotizaBot Pro Highlight */}
          <div className="mt-16 max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200">
              <CardContent className="py-8">
                <div className="text-center mb-6">
                  <Badge className="bg-amber-500 text-white mb-3">PLAN PRO</Badge>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    ¡Cobra a tus clientes directamente por WhatsApp!
                  </h2>
                  <p className="text-slate-600">
                    Configura tus datos bancarios y recibe pagos automáticamente
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CreditCard className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1">Mercado Pago</h4>
                    <p className="text-sm text-slate-600">
                      Genera links de pago automáticos y recibe con tarjeta
                    </p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Building2 className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1">SPEI / Transferencia</h4>
                    <p className="text-sm text-slate-600">
                      Envía tu CLABE automáticamente al confirmar cotización
                    </p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Bell className="w-6 h-6 text-amber-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-1">Notificaciones</h4>
                    <p className="text-sm text-slate-600">
                      Recibe aviso cuando te paguen a tu WhatsApp o email
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Comparison Table */}
          <div className="mt-16 max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">
              Comparación Detallada
            </h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-4 text-left text-sm font-semibold text-slate-900">Característica</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-emerald-600">CotizaBot</th>
                    <th className="px-4 py-4 text-center text-sm font-semibold text-amber-600">CotizaBot Pro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-700">Cotizaciones</td>
                    <td className="px-4 py-3 text-center text-sm text-emerald-600 font-medium">Ilimitadas</td>
                    <td className="px-4 py-3 text-center text-sm text-amber-600 font-medium">Ilimitadas</td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">WhatsApp integrado</td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-700">QR y Link propio</td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">Dashboard completo</td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-700 font-medium">Cobros Mercado Pago</td>
                    <td className="px-4 py-3 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700 font-medium">Cobros SPEI</td>
                    <td className="px-4 py-3 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-700 font-medium">Notificaciones de pago</td>
                    <td className="px-4 py-3 text-center"><X className="w-5 h-5 text-slate-300 mx-auto" /></td>
                    <td className="px-4 py-3 text-center"><Check className="w-5 h-5 text-amber-500 mx-auto" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-700">Soporte</td>
                    <td className="px-4 py-3 text-center text-sm text-emerald-600">Prioritario</td>
                    <td className="px-4 py-3 text-center text-sm text-amber-600">VIP</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-3 text-sm text-slate-900 font-bold">Precio mensual</td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-emerald-600">$1,000</td>
                    <td className="px-4 py-3 text-center text-sm font-bold text-amber-600">$2,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-500 mb-2">Métodos de pago aceptados</p>
            <div className="flex items-center justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#1A1F71"/>
                  <text x="4" y="16" fill="white" fontSize="8" fontWeight="bold">VISA</text>
                </svg>
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#EB001B"/>
                  <circle cx="9" cy="12" r="5" fill="#EB001B"/>
                  <circle cx="15" cy="12" r="5" fill="#F79E1B"/>
                </svg>
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#006FCF"/>
                  <text x="2" y="15" fill="white" fontSize="6" fontWeight="bold">AMEX</text>
                </svg>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Pagos seguros procesados por Mercado Pago</p>
          </div>

          {/* Mexican company badge */}
          <div className="mt-8 max-w-2xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <span className="text-2xl">🇲🇽</span>
                <h3 className="text-lg font-bold text-green-900">Empresa 100% Mexicana</h3>
              </div>
              <p className="text-sm text-green-800">
                Emitimos <strong>factura fiscal (CFDI)</strong> deducible de impuestos.
                Soporte en español, precios en pesos mexicanos, sin cargos en dólares.
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-center text-slate-900 mb-8">
              Preguntas Frecuentes
            </h2>
            <div className="space-y-4">
              <Card>
                <CardContent className="py-4">
                  <h3 className="font-bold text-slate-900 mb-2">¿Puedo pagar con tarjeta?</h3>
                  <p className="text-sm text-slate-600">
                    Sí. Aceptamos Visa, Mastercard, American Express, débito, Oxxo, SPEI y saldo de Mercado Pago. Los pagos se procesan de forma segura por Mercado Pago.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-4">
                  <h3 className="font-bold text-slate-900 mb-2">¿Puedo facturar? ¿Es deducible?</h3>
                  <p className="text-sm text-slate-600">
                    Sí. Emitimos factura CFDI a tu RFC. Tu suscripción es 100% deducible como gasto de operación. Solicita tu factura por WhatsApp o email después de tu pago.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-4">
                  <h3 className="font-bold text-slate-900 mb-2">¿Cómo funciona el cobro con CotizaBot Pro?</h3>
                  <p className="text-sm text-slate-600">
                    Configuras tu CLABE y/o Mercado Pago en tu panel. Cuando confirmas una cotización, el bot envía automáticamente los datos de pago a tu cliente.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="py-4">
                  <h3 className="font-bold text-slate-900 mb-2">¿Recibo notificación cuando me pagan?</h3>
                  <p className="text-sm text-slate-600">
                    Sí, con CotizaBot Pro puedes configurar un número de WhatsApp o email para recibir alertas de pago.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          {!isAuthenticated && (
            <div className="mt-16 text-center">
              <Link to="/registro">
                <Button size="lg" className="text-lg px-8 py-6 bg-emerald-600 hover:bg-emerald-700">
                  Crear Cuenta y Empezar
                </Button>
              </Link>
              <p className="text-sm text-slate-500 mt-4">
                Cancela cuando quieras • Factura CFDI deducible • Soporte en español 🇲🇽
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="/logo-cotizaexpress.png" 
                alt="CotizaBot" 
                className="h-8 w-auto brightness-0 invert"
              />
              <span className="text-white font-semibold">CotizaBot</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="tel:+528130783171" className="hover:text-white">+52 81 3078 3171</a>
              <a href="mailto:contacto@arobegroup.com" className="hover:text-white">contacto@arobegroup.com</a>
            </div>
          </div>
          <div className="text-center mt-6">
            <p className="text-slate-500 text-xs">
              © 2026 CotizaExpress.com - CotizaBot by CotizaExpress.com
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
